'use client';
/** @jsxImportSource @emotion/react */

import { useState } from 'react';

import type { StaticDateRangePickerProps as MuiStaticDateRangePickerProps } from '@mui/x-date-pickers-pro/StaticDateRangePicker';

import { useTheme, alpha } from '@mui/material/styles';
import {
  type DateRange,
  pickersLayoutClasses,
  PickersLayoutContentWrapper,
  type PickersLayoutProps,
  PickersLayoutRoot,
  usePickerLayout,
  type PickerValidDate,
} from '@mui/x-date-pickers-pro';
import { StaticDateRangePicker as MuiStaticDateRangePicker } from '@mui/x-date-pickers-pro/StaticDateRangePicker';

import { Divider } from '../../divider/Divider';
import { Shortcuts, type ShortcutsProps } from '../shortcuts/Shortcuts';

function CustomLayout<TDate extends PickerValidDate>(
  props: PickersLayoutProps<DateRange<TDate>, TDate, 'day'>
) {
  const { shortcuts, content } = usePickerLayout(props);
  const theme = useTheme();

  return (
    <PickersLayoutRoot
      ownerState={{ isLandscape: false }}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',

        // Hide unwanted UI elements
        '.MuiPickersToolbar-root, .MuiDialogActions-root': {
          display: 'none',
        },

        // Month container layout
        '.MuiDateRangeCalendar-monthContainer': {
          flexGrow: 1,
          '&:not(:last-of-type)': {
            border: 'none',
          },
        },

        // Calendar header
        '.MuiPickersCalendarHeader-root': {
          padding: `${theme.spacing(1)} ${theme.spacing(1.5)} ${theme.spacing(1)} ${theme.spacing(1.5)}`,
          margin: 0,
          minHeight: theme.spacing(7),
        },

        // Creates 8px gap between arrow buttons
        '.MuiPickersArrowSwitcher-spacer': {
          width: theme.spacing(4),
        },

        // Day Calendar header
        '.MuiDayCalendar-header': {
          padding: `0 ${theme.spacing(2)} 0 ${theme.spacing(2)}`,
        },

        // Week day labels
        '.MuiDayCalendar-weekDayLabel': {
          flexGrow: 1,
        },

        // Calendar responsive sizing
        '.MuiDayCalendar-slideTransition': {
          minWidth: 'unset',
          overflow: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        },

        // Week layout
        '.MuiDayCalendar-weekContainer': {
          margin: 0,
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
          paddingBottom: theme.spacing(0.25),
          '&:last-of-type': {
            paddingBottom: theme.spacing(1),
          },
          '&:first-of-type': {
            paddingTop: theme.spacing(1),
          },
        },

        // Day cells - base styling
        '.MuiDateRangePickerDay-root': {
          flexGrow: 1,
          flexBasis: 0,
          borderRadius: 0,
          height: theme.spacing(5),
          width: '100%',
          '&:first-of-type, &:last-of-type': {
            borderRadius: 0,
          },
        },

        // Day button - base styling
        '.MuiPickersDay-root': {
          width: '100%',
          height: '100%',
          borderRadius: 0,
          margin: '0 4%', // to prevent strange overlapping behaviour of days when using range interval - has to be a percentage so that it works at different screen sizes.
        },

        // Day button - remove borders
        '.MuiDateRangePickerDay-day': {
          border: 'none !important',
        },

        // Day button - focus state
        '.MuiDateRangePickerDay-day:focus:not(.Mui-selected)': {
          backgroundColor: 'unset',
        },

        // Day button - selected state border radius
        '.MuiDateRangePickerDay-rangeIntervalDayHighlightStart .MuiDateRangePickerDay-day.Mui-selected':
          {
            borderRadius: `${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px`,
          },
        '.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd .MuiDateRangePickerDay-day.Mui-selected':
          {
            borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
          },

        // When start and end are the same day, use full border radius
        '.MuiDateRangePickerDay-rangeIntervalDayHighlightStart.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd .MuiDateRangePickerDay-day.Mui-selected':
          {
            borderRadius: `${theme.shape.borderRadius}px`,
          },

        // When only start is selected (before end is picked), apply start border radius
        '.MuiPickersDay-root.Mui-selected': {
          borderRadius: `${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px`,
        },

        // Override for end date when both are selected
        '.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd .MuiPickersDay-root.Mui-selected':
          {
            borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
          },

        // Override for same day selection
        '.MuiDateRangePickerDay-rangeIntervalDayHighlightStart.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd .MuiPickersDay-root.Mui-selected':
          {
            borderRadius: `${theme.shape.borderRadius}px`,
          },

        // Today styling
        '.MuiPickersDay-today': {
          color: theme.palette.primary.main,
          fontWeight: theme.typography.fontWeightBold,
          '&.Mui-selected': {
            color: theme.palette.common.white,
          },
        },

        // Range interval preview - base styling
        '.MuiDateRangePickerDay-rangeIntervalPreview': {
          display: 'flex',
          justifyContent: 'center',
          borderRadius: 0,
          height: '100%',
          width: '100%',
          border: 'none',
          borderTop: `${theme.spacing(0.25)} solid transparent`,
          borderBottom: `${theme.spacing(0.25)} solid transparent`,
          '&:first-of-type, &:last-of-type': {
            borderRadius: '0 !important',
          },
        },

        // Range interval - preview state (hovering)
        '.MuiDateRangePickerDay-rangeIntervalDayPreview': {
          backgroundColor: alpha(
            theme.palette.common.black,
            theme.palette.action.selectedOpacity
          ),
        },

        // Range interval - selected state (after both dates picked)
        '.MuiDateRangePickerDay-dayInsideRangeInterval, .MuiDateRangePickerDay-rangeIntervalDayHighlight, .MuiDateRangePickerDay-rangeIntervalDayHighlightStart, .MuiDateRangePickerDay-rangeIntervalDayHighlightEnd':
          {
            backgroundColor: alpha(
              theme.palette.common.black,
              theme.palette.action.selectedOpacity
            ),
          },

        // Range interval - match border radius to selected buttons
        '.MuiDateRangePickerDay-rangeIntervalDayHighlightStart': {
          borderRadius: `${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px`,
        },
        '.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd': {
          borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
        },

        // Range interval - remove button background to prevent double-alpha stacking (not on selected endpoints)
        '.MuiDateRangePickerDay-rangeIntervalDayHighlight .MuiDateRangePickerDay-day:not(.Mui-selected), .MuiDateRangePickerDay-rangeIntervalDayHighlightStart .MuiDateRangePickerDay-day:not(.Mui-selected), .MuiDateRangePickerDay-rangeIntervalDayHighlightEnd .MuiDateRangePickerDay-day:not(.Mui-selected)':
          {
            backgroundColor: 'transparent !important',
          },

        // Hover styles - wrapped in media query to prevent touch device activation
        '@media (hover: hover)': {
          // Day button - hover state (not when selected or in range preview)
          '.MuiDateRangePickerDay-day:hover:not(.Mui-selected)': {
            backgroundColor: alpha(
              theme.palette.common.black,
              theme.palette.action.selectedOpacity
            ),
          },

          // Day button - remove hover when inside range preview
          '.MuiDateRangePickerDay-rangeIntervalDayPreview .MuiDateRangePickerDay-day:hover:not(.Mui-selected), .MuiDateRangePickerDay-rangeIntervalDayPreviewEnd .MuiDateRangePickerDay-day:hover:not(.Mui-selected)':
            {
              backgroundColor: 'transparent',
            },

          // Range interval - hover on preview end
          '.MuiDateRangePickerDay-rangeIntervalDayPreviewEnd:hover:not(.Mui-selected):not(:has(.MuiPickersDay-hiddenDaySpacingFiller))':
            {
              backgroundColor: alpha(
                theme.palette.common.black,
                theme.palette.action.selectedOpacity
              ),
            },

          // Days outside range interval - hover state
          '.MuiDateRangePickerDay-dayOutsideRangeInterval:hover:not(.Mui-selected)':
            {
              backgroundColor: alpha(
                theme.palette.common.black,
                theme.palette.action.selectedOpacity
              ),
            },

          // Disabled days - no hover background
          '.MuiDateRangePickerDay-rangeIntervalPreview:hover:has(.Mui-disabled)':
            {
              backgroundColor: 'transparent !important',
            },
        },

        // Focus styles - remove default outline
        '.MuiPickersDay-root.Mui-focusVisible': {
          outline: 'none',
        },

        // Focus styles - custom focus ring
        '.MuiDateRangePickerDay-day.Mui-focusVisible::after': {
          borderRadius: theme.ui.focus.borderRadius,
          content: '""',
          outlineOffset: theme.spacing(0.5), // can't use outline-offset token as it is only 2px
          outline: `${theme.ui.focus.outlineWidth} solid ${theme.palette.action.focusOutline}`,
          position: 'absolute',
          pointerEvents: 'none',
        },
      }}
    >
      <PickersLayoutContentWrapper
        className={pickersLayoutClasses.contentWrapper}
        sx={{
          overflow: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {content}
      </PickersLayoutContentWrapper>
      <Divider />

      {shortcuts}
    </PickersLayoutRoot>
  );
}

/**
 * `MobileDateRangePickerProps` restricts customisation of the `shortcuts` and `layout` slots.
 * These slots are always set to the built-in `Shortcuts` and `CustomLayout` components managed by the Design System team.
 * Consumers cannot override these slots.
 */
type MobileDateRangePickerProps<TDate extends PickerValidDate> = Omit<
  MuiStaticDateRangePickerProps<TDate>,
  'slots' | 'slotProps'
> & {
  slots?: Omit<
    NonNullable<MuiStaticDateRangePickerProps<TDate>['slots']>,
    'shortcuts' | 'layout'
  >;
  slotProps?: Omit<
    MuiStaticDateRangePickerProps<TDate>['slotProps'],
    'shortcuts'
  > & {
    shortcuts?: ShortcutsProps<DateRange<TDate>>;
  };
};

export type { MobileDateRangePickerProps };

export function MobileDateRangePicker<TDate extends PickerValidDate>(
  props: MobileDateRangePickerProps<TDate>
) {
  const [internalValue, setInternalValue] = useState<DateRange<TDate>>(
    props.defaultValue ?? [null, null]
  );

  const currentValue = props.value ?? internalValue;

  const handleChange: MuiStaticDateRangePickerProps<TDate>['onChange'] = (
    newValue,
    context
  ) => {
    if (props.value === undefined) {
      setInternalValue(newValue);
    }

    props.onChange?.(newValue, context);
  };

  return (
    <MuiStaticDateRangePicker
      {...props}
      onChange={handleChange}
      slots={{
        ...props.slots,
        shortcuts: Shortcuts,
        layout: (layoutProps) => <CustomLayout {...layoutProps} />,
      }}
      slotProps={{
        ...props.slotProps,
        shortcuts: {
          value: currentValue,
          ...props.slotProps?.shortcuts,
        },
      }}
    />
  );
}
